import React, { useState, useMemo } from "react";
import useTitanApp from "../../../hooks/useTitanApp";
import {
  setShowStartConsultationModal,
  setShowConsultationTypeModal,
  setConsultationStarted,
  setPatientTranscription,
  setReferralLetter,
  setIsSummaryGenerated,
  setConsultationId,
  updateConsultationTemplate,
  addUpdatedTranscription,
  setTranscript,
  setConsultationStopAfterRefresh,
  setFreeTypeMode,
  updateConsultationButtonStatus,
  setIsLocallyCompleted,
  trackServiceBilling,
} from "../../../dispatchers/patientDispatchers";
import {
  Modal,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import CustomSelect from "../../controls/CustomSelect";
import { IoClose } from "react-icons/io5";
import { RxPencil1 } from "react-icons/rx";
import {
  sendAIQAService,
  setAiqa,
  setFullTranscript,
} from "../../../dispatchers/templateDispatchers";
import { ActionTrackForConsultations } from "../../../data/actionTrackConsultations";
import { ConsultationButtonStatus } from "../../../enum/ConsultationButtonStatus";
import { setConsultationLoadError } from "../../../dispatchers/appDispatchers";

const languages = [
  { label: "English", value: "en" },
  { label: "Urdu", value: "ur" },
  { label: "Spanish", value: "es" },
  { label: "Arabic", value: "ar" },
  { label: "French", value: "fr" },
];

export default function PatientConsultationTypeModal({ stopTranscription }) {
  const { appState, patientState, templateState } = useTitanApp();

  const {
    darkMode,
    currentUser,
    currentPharmacy,
    company,
    companyId,
    currentPharmacyId,
  } = appState || {};
  const {
    showConsultationTypeModal,
    consultationId,
    hydratedTemplateSections,
  } = patientState || {};
  const { selectedTemplate, fullTranscript, isAIQALoading } =
    templateState || {};

  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]);
  const [transcription, setTranscription] = useState("");

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "50%",
    bgcolor: darkMode ? "#222" : "#fff",
    boxShadow: 24,
    borderRadius: "16px",
  };

  const allUserControls = Array.isArray(selectedTemplate.templateSections)
    ? selectedTemplate.templateSections.flatMap((section) => {
        return section.userControls;
      })
    : [];

  const questions = Array.isArray(allUserControls)
    ? allUserControls.map((ctrl, idx) => {
        return {
          [`q${idx + 1}`]: ctrl.label,
        };
      })
    : [];

  const handleRemoveClick = () => {
    setShowStartConsultationModal(false);
    setShowConsultationTypeModal(false);
  };

  const handleSubmit = async () => {
    try {
      setAiqa([]);
      setFullTranscript([]);
      setIsSummaryGenerated(false);
      setConsultationStopAfterRefresh(false);
      setReferralLetter("");
      setTranscript(transcription);
      const data = {
        q: questions,
        t: transcription,
      };

      trackServiceBilling({
        companyId,
        consultationId,
        pharmacyId: currentPharmacyId,
        actiontype: "FreeType",
      });
      await sendAIQAService(data).then(setFreeTypeMode(true));
      const statusData = {
        consultationId: consultationId,
        buttonStatus: ConsultationButtonStatus.Completed,
      };
      const success = await updateConsultationButtonStatus(statusData);
      if (success) {
        setIsLocallyCompleted(true);
      }
      if (!isAIQALoading) {
        handleRemoveClick();
      }
      stopTranscription();

      await ActionTrackForConsultations(
        2,
        currentUser,
        currentPharmacy,
        company,
      );
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setConsultationLoadError(true);
    }
  };

  const textareaStyle = useMemo(
    () => ({
      width: "100%",
      resize: "vertical",
      padding: "12px",
      borderRadius: "16px",
      border: "none",
      backgroundColor: darkMode ? "#000" : "#fff",
      color: darkMode ? "#f2f2f2" : "#222",
      maxHeight: "350px",
      overflowY: "auto",
      fontFamily: "inherit",
      fontSize: "14px",
    }),
    [darkMode],
  );

  return (
    <Modal open={showConsultationTypeModal}>
      <Box sx={style}>
        <div className="pc-actions-modal-wrapper free-transcription-content">
          <div className="pc-actions-modal-header">
            <div className="flex items-center gap-2">
              <RxPencil1 size={18} />
              <h3 className="text-[18px] font-semibold">
                Free type transcription
              </h3>
            </div>

            <IoClose
              className="close-icon"
              onClick={handleRemoveClick}
              color={darkMode ? "#f2f2f2" : "#222"}
            />
          </div>
          <div className="pc-actions-modal-content">
            <div className="form-group">
              <div className="form-group">
                <label>Conversation transcription</label>
                <div className="type-transciption-textarea-wrapper">
                  <textarea
                    value={transcription}
                    onChange={(e) => setTranscription(e.target.value)}
                    rows={15}
                    style={textareaStyle}
                    required
                  />
                </div>
                <div>
                  <span>{transcription.length} characters</span>
                </div>
              </div>
            </div>
            <div className="free-transcription-footer">
              <button className="cancel-btn" onClick={handleRemoveClick}>
                Cancel
              </button>
              <button
                className="add-btn"
                onClick={handleSubmit}
                disabled={isAIQALoading}
              >
                {isAIQALoading ? "Processing..." : "Add to Consultation"}
              </button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
}
